import { Access, FrequentEmoji, Huddles, Messages, Profile, RichText as RT, type RichTextContext, type TimelineItem } from "@bundle/core";
import type { Member } from "@bundle/contracts";
import { useContext, useState, type ReactNode } from "react";
import { Avatar, Button, EmojiPicker, FrequentEmojiContext, Icon, InlineText, Popover, Pressable, Spacer, Stack, Text, useAnnouncer, type MenuItem } from "../primitives/index.ts";
import { useFormat, useLocale, useT, useTr } from "../i18n/I18nProvider.tsx";
import { useMemberCard } from "../context/SettingsContext.tsx";
import { AgentBadge, AppBadge, ExternalBadge } from "./Badges.tsx";
import { StatusEmoji } from "./StatusEmoji.tsx";
import { MessageFiles, type FileActions } from "./MessageFiles.tsx";
import { GifMessage, StickerMessage } from "./MessageMedia.tsx";
import { ReactionChips } from "./ReactionChips.tsx";
import { PinnedLine, SavedLine, SharedQuote, UnfurlCard, withMessageExtras, type MessageExtras } from "./MessageExtras.tsx";
import { RichText } from "./RichText.tsx";
import { BlockKit } from "./BlockKit.tsx";
import type { BlockKit as BK } from "@bundle/core";
import { HuddleRecapCard } from "./huddle/HuddleRecapCard.tsx";
import { MissedHuddleLine } from "./huddle/MissedHuddleLine.tsx";

export type MessageItem = Extract<TimelineItem, { type: "message" }>;

export type MessageRowProps = {
  item: MessageItem;
  author?: Member;
  meId: string;
  ctx: RichTextContext;
  /** Show "3 replies" under thread roots (channel view). */
  showThreadSummary: boolean;
  /** Offline: pending messages read "Waiting to send". */
  offline: boolean;
  onToggleReaction: (emoji: string) => void;
  /** Opens the thread: the message's own, or its root's for a reply also sent to the channel (its context line too). */
  onReply?: () => void;
  onRetry: () => void;
  onDiscard: () => void;
  onUserPress?: (userId: string) => void;
  onChannelPress?: (channelId: string) => void;
  /** A user group mention was clicked (open the group card). */
  onGroupPress?: (groupId: string) => void;
  /** What the message's files can do (download, copy link, details); files render only with it. */
  fileActions?: FileActions;
  /** Pending message waiting for its uploads: combined progress 0…1. */
  uploadProgress?: number;
  /** Tap on a sticker: open its pack (absent = not tappable). */
  onStickerPress?: (packId: string) => void;
  /** Thread view: the context line of replies that were also sent to the channel ("Also sent to #design"). */
  alsoSentLabel?: string;
  /** "Edit message" in the "…" menu, above Delete (Slack; my own messages; ChannelViewModel.canEdit); absent = not offered. */
  onEdit?: () => void;
  /**
   * "Delete message" in the hover toolbar's "…" menu and the row's context menu (ChannelViewModel.canDelete: mine, or
   * anyone's for owners and admins); it should ask first (askDelete). Absent = not offered.
   */
  onDelete?: () => void;
  /** This message is being edited: the edit composer shown in place of its body (hover actions hidden meanwhile). */
  editor?: ReactNode;
  /** Copy link, Pin, Mark unread in the "…" menu, and the "Pinned by …" line (MessageExtras.tsx). */
  extras?: MessageExtras;
  /**
   * Thread follow in the "…" menu (Slack): "Get notified about new replies" / "Turn off notifications for replies".
   * `following` = I follow the message's thread now; `onToggle` flips it. Absent = not offered.
   */
  follow?: { following: boolean; onToggle: () => void };
  /** The conversation is a 1:1 DM (older huddle messages there count as missed when I wasn't in them). */
  oneToOneDm?: boolean;
  /** "Call back" on a missed huddle (HuddleViewModel.callBack with who rang me); absent = not offered. */
  onHuddleCallBack?: (callerId: string) => void;
  /** A Block Kit button on this message was pressed (ChannelViewModel.pressButton); absent = buttons disabled. */
  onKitButton?: (blockId: string, button: BK.KitButtonNode, values: BK.KitValues) => void;
  /** A select / picker / overflow on this message changed (ChannelViewModel.changeElement). */
  onKitElement?: (blockId: string, element: BK.KitValueNode | BK.KitOverflowNode, value: string[], values: BK.KitValues) => void;
};

/**
 * The huddle message's body: "Missed huddle" with Call back for someone rung who never joined (Slack), above the
 * card only when the card has more to show (notes, a transcript); otherwise the recap card.
 */
function HuddleMessage(p: MessageRowProps) {
  const m = p.item.message;
  const recap = m.huddle!;
  const missed = Huddles.missedHuddle(recap, p.meId, { oneToOne: p.oneToOneDm === true, authorId: m.authorId });
  const card = <HuddleRecapCard recap={recap} members={p.ctx.members} testID={`huddle-recap-${p.item.key}`} />;
  if (missed === undefined) return card;
  const onCallBack = p.onHuddleCallBack;
  return (
    <>
      <MissedHuddleLine
        caller={p.ctx.members[missed.callerId]}
        onCallBack={onCallBack === undefined ? undefined : () => onCallBack(missed.callerId)}
        testID={`huddle-missed-${p.item.key}`}
      />
      {recap.transcript.length > 0 || recap.notes !== undefined || recap.notesPending === true ? card : null}
    </>
  );
}

function ThreadSummary({ item, ctx, onReply }: { item: MessageItem; ctx: RichTextContext; onReply: () => void }) {
  const t = useT();
  const fmt = useFormat();
  const m = item.message;
  const tldr = m.summary?.bullets ?? [];
  return (
    <Stack gap="0.5">
      <Pressable direction="horizontal" radius="md" onPress={onReply} testID={`thread-summary-${m.id}`}>
        <Stack direction="horizontal" align="center" gap="2" paddingX="1" paddingY="0.5">
          <Stack direction="horizontal" align="center" gap="0.5">
            {m.replyUserIds.slice(0, 3).map((id) => {
              const u = ctx.members[id];
              return <Avatar key={id} name={u === undefined ? id : u.displayName} src={u?.avatarUrl} size="avatar.xs" square={u?.kind === "agent"} />;
            })}
          </Stack>
          <Text variant="footnote" weight="semibold" color="text.link">
            {t("message.replies", { count: m.replyCount })}
          </Text>
          {m.lastReplyAt === undefined ? null : (
            <Text variant="caption" color="text.tertiary">
              {t("message.lastReply", { time: fmt.time(m.lastReplyAt) })}
            </Text>
          )}
        </Stack>
      </Pressable>
      {/* The thread's AI TL;DR (logic/threadSummary), compact; the full card with Refresh is in the thread pane. */}
      {tldr.length === 0 ? null : (
        <Pressable radius="md" onPress={onReply} accessibilityLabel={`${t("summary.title")}: ${tldr.join(" ")}`} testID={`thread-tldr-${m.id}`}>
          <Stack direction="horizontal" align="start" gap="1.5" paddingX="1" paddingY="0.5">
            <Icon name="sparkles" size="icon.sm" color="accent.text" />
            <Stack grow gap="0.5">
              {tldr.map((line, i) => (
                <Text key={i} variant="footnote" color="text.secondary" lines={2}>
                  {`• ${line}`}
                </Text>
              ))}
            </Stack>
          </Stack>
        </Pressable>
      )}
    </Stack>
  );
}

function SendState({ item, offline, uploadProgress, onRetry, onDiscard }: { item: MessageItem; offline: boolean; uploadProgress?: number; onRetry: () => void; onDiscard: () => void }) {
  const t = useT();
  if (item.status === "sending") {
    return (
      <Stack direction="horizontal" align="center" gap="1" testID={`pending-${item.key}`}>
        <Icon name="clock" size="icon.sm" color="text.tertiary" />
        <Text variant="caption" color="text.tertiary">
          {offline ? t("message.queued") : uploadProgress !== undefined && uploadProgress < 1 ? t("message.uploading", { percent: Math.floor(uploadProgress * 100) }) : t("message.sending")}
        </Text>
      </Stack>
    );
  }
  return (
    <Stack direction="horizontal" align="center" gap="2" testID={`failed-${item.key}`}>
      <Icon name="alert" size="icon.sm" color="status.danger" />
      <Text variant="caption" weight="medium" color="status.danger">
        {t("message.failed")}
      </Text>
      <Button label={t("message.retry")} variant="ghost" size="sm" icon="reload" onPress={onRetry} testID={`retry-${item.key}`} />
      <Button label={t("message.discard")} variant="ghost" size="sm" onPress={onDiscard} testID={`discard-${item.key}`} />
    </Stack>
  );
}

/** The small line above a message's header: "replied to a thread: …" (opens it) or "Also sent to #design". */
function ContextLine({ item, alsoSent, onReply }: { item: MessageItem; alsoSent?: string; onReply?: () => void }) {
  const tr = useTr();
  if (item.broadcast !== undefined) {
    const label = tr(item.broadcast.label);
    const line = (
      <Text variant="caption" color="text.secondary" lines={1}>
        {label}
      </Text>
    );
    if (onReply === undefined) return line;
    return (
      <Pressable direction="horizontal" radius="sm" onPress={onReply} accessibilityLabel={label} testID={`thread-broadcast-${item.key}`}>
        {line}
      </Pressable>
    );
  }
  return (
    <Text variant="caption" color="text.tertiary" lines={1} testID={`also-sent-${item.key}`}>
      {alsoSent ?? ""}
    </Text>
  );
}

/** The author's name/avatar opens their profile card where settings are on (MemberCardContext); plain otherwise. */
function AuthorPress({ authorId, label, children }: { authorId: string | undefined; label: string; children: ReactNode }) {
  const card = useMemberCard();
  if (card === null || authorId === undefined) return <>{children}</>;
  return (
    <Pressable radius="sm" onPress={() => card.open(authorId)} accessibilityLabel={label} testID={`author-${authorId}`}>
      {children}
    </Pressable>
  );
}

/** One message: context line (thread replies also sent), avatar + name + time header (unless grouped), body, reactions, thread summary, send state. */
export function MessageRow(p: MessageRowProps) {
  const t = useT();
  const tr = useTr();
  const fmt = useFormat();
  const locale = useLocale();
  const announce = useAnnouncer();
  const [picking, setPicking] = useState(false);
  // The chip my latest reaction landed on: it pops and scrolls into view (seq: the same emoji again pops again).
  const [attention, setAttention] = useState<{ emoji: string; seq: number } | undefined>(undefined);
  const m = p.item.message;
  const frequent = useContext(FrequentEmojiContext);
  const mine = Messages.myReactions(m, p.meId);
  const react = (emoji: string) => {
    const toggle = Messages.reactionToggle(m, p.meId, emoji);
    setAttention((a) => (toggle.add ? { emoji, seq: (a?.seq ?? 0) + 1 } : a?.emoji === emoji ? undefined : a));
    announce(tr(toggle.announcement));
    p.onToggleReaction(emoji);
  };
  // An integration's message (incoming webhook, channel reminder) shows the integration, not the member behind it.
  const author = m.app === undefined ? p.author : undefined;
  const name = author === undefined ? (m.app === undefined ? m.authorId : m.app.name) : author.displayName;
  const isAgent = author !== undefined && author.kind === "agent";
  const external = author !== undefined && Access.isExternal(author);
  const sent = p.item.status === "sent";
  const authorLook = author === undefined ? {} : Profile.avatarLook(author);
  const alsoSent = m.threadRootId !== undefined && m.alsoSentToChannel === true ? p.alsoSentLabel : undefined;
  const hasContext = p.item.broadcast !== undefined || alsoSent !== undefined;
  // A row with a context line always shows its header.
  const grouped = p.item.grouped && !hasContext;

  const actions: MenuItem[] = [];
  const editing = p.editor !== undefined;
  if (sent && !m.deleted && !editing) {
    // Quick reactions are toggles: the ones I added show active, and pressing one again removes it.
    // Slack: the hover bar's three are my most used emoji ("Frequently used"; the defaults until I have some).
    for (const emoji of frequent === null ? RT.QUICK_REACTIONS.slice(0, 3) : FrequentEmoji.quickReactions(frequent.emoji)) {
      actions.push({ id: `react-${emoji}`, label: emoji, selected: mine.includes(emoji), onSelect: () => {
        if (!mine.includes(emoji)) frequent?.onUse(emoji, false);
        react(emoji);
      } });
    }
    actions.push({ id: "react", label: t("message.addReaction"), icon: "reaction", onSelect: () => setPicking(true) });
    if (p.onReply !== undefined) {
      const reply = p.onReply;
      actions.push({ id: "reply", label: t("message.reply"), icon: "thread", onSelect: reply });
    }
    // Slack's hover bar: "Forward message" (share) and "Save for later" (a toggle: filled while saved); Edit lives in
    // the "…" menu, not the bar.
    const share = p.extras?.onShare;
    if (share !== undefined) actions.push({ id: "share", label: t("message.share"), icon: "arrowRight", onSelect: share });
    const later = p.extras?.later;
    if (later !== undefined) actions.push({ id: "save-later", label: t(later.saved ? "message.removeFromLater" : "message.saveForLater"), icon: "bookmark", selected: later.saved, onSelect: later.onToggle });
  }
  const moreItems: MenuItem[] = [];
  if (sent && !m.deleted && !editing && p.follow !== undefined) {
    const f = p.follow;
    moreItems.push({ id: "follow", label: t(f.following ? "thread.follow.off" : "thread.follow.on"), icon: f.following ? "bellOff" : "bell", onSelect: f.onToggle });
  }
  if (sent && !m.deleted && !editing && p.onEdit !== undefined) moreItems.push({ id: "edit", label: t("message.edit"), icon: "edit", onSelect: p.onEdit });
  if (sent && !m.deleted && !editing && p.onDelete !== undefined) moreItems.push({ id: "delete", label: t("message.delete"), icon: "trash", destructive: true, onSelect: p.onDelete });
  const more = moreItems.length > 0 ? { label: t("message.more"), items: moreItems } : undefined;

  return (
    <Stack
      gap="0.5"
      paddingX="5"
      paddingY={grouped && !editing ? "0.5" : "1.5"}
      background={editing ? "bg.selected" : undefined}
      hoverHighlight={!editing}
      hoverActions={actions}
      moreActions={editing || !sent || m.deleted ? more : withMessageExtras(more, p.extras, t)}
      testID={`message-${p.item.key}`}
    >
      {p.extras?.later?.saved === true && !m.deleted ? (
        <Stack direction="horizontal" align="center" gap="3">
          <Spacer size="9" />
          <SavedLine testID={`saved-${p.item.key}`} />
        </Stack>
      ) : null}
      {p.extras?.pinnedByName !== undefined && !m.deleted ? (
        <Stack direction="horizontal" align="center" gap="3">
          <Spacer size="9" />
          <PinnedLine name={p.extras.pinnedByName} testID={`pinned-${p.item.key}`} />
        </Stack>
      ) : null}
      {hasContext ? (
        <Stack direction="horizontal" align="center" gap="3">
          <Spacer size="9" />
          <ContextLine item={p.item} alsoSent={alsoSent} onReply={p.onReply} />
        </Stack>
      ) : null}
      <Stack direction="horizontal" align="start" gap="3">
        <Popover
          open={picking}
          onClose={() => setPicking(false)}
          side="bottom"
          align="start"
          anchor={
            grouped ? (
              <Spacer size="9" />
            ) : (
              <AuthorPress authorId={author?.userId} label={name}>
              <Avatar name={name} src={author === undefined ? m.app?.iconUrl : authorLook.src} tone={authorLook.tone} emoji={authorLook.emoji} size="avatar.message" square={isAgent || m.app !== undefined} />
            </AuthorPress>
            )
          }
        >
          <EmojiPicker
            report={false}
            locale={locale}
            searchPlaceholder={t("picker.emoji.search")}
            onSelect={(e) => {
              setPicking(false);
              react(e);
            }}
          />
        </Popover>
        <Stack grow gap="1">
          {grouped ? null : (
            <Stack direction="horizontal" align="center" gap="2" wrap>
              <AuthorPress authorId={author?.userId} label={name}>
                <Text weight="semibold" lines={1}>
                  {name}
                </Text>
              </AuthorPress>
              {/* Slack: the author's current status (not the one when they sent it). */}
              {author === undefined ? null : <StatusEmoji person={author} size="sm" testID={`message-status-${p.item.key}`} />}
              {isAgent ? <AgentBadge /> : null}
              {m.app !== undefined ? <AppBadge /> : null}
              {external ? <ExternalBadge organization={Access.organizationName(author!)} /> : null}
              <Text variant="caption" color="text.tertiary">
                {fmt.time(m.createdAt)}
              </Text>
              {m.editedAt === undefined || m.deleted ? null : (
                <Text variant="caption" color="text.tertiary" testID={`edited-${p.item.key}`}>
                  {t("message.edited")}
                </Text>
              )}
            </Stack>
          )}
          {m.deleted ? (
            <Stack testID={`deleted-${p.item.key}`}>
              <InlineText runs={[{ text: t("message.deleted"), italic: true }]} color="text.tertiary" />
            </Stack>
          ) : editing ? (
            // Kept fully in view while open: the bottom message's editor would otherwise sit half under the composer.
            <Stack keepInView>{p.editor}</Stack>
          ) : m.huddle !== undefined ? null : m.appBlocks !== undefined && m.appBlocks.length > 0 ? (
            // Block Kit: `text` is the fallback (notifications, search), so only the blocks show, like Slack.
            <BlockKit blocks={m.appBlocks} ctx={p.ctx} container={{ messageId: m.id }} onButton={sent ? p.onKitButton : undefined} onElement={sent ? p.onKitElement : undefined} channelId={m.channelId} onUserPress={p.onUserPress} onChannelPress={p.onChannelPress} testID={`kit-${p.item.key}`} />
          ) : (
            <RichText blocks={m.blocks} ctx={p.ctx} onUserPress={p.onUserPress} onChannelPress={p.onChannelPress} onGroupPress={p.onGroupPress} jumbo={RT.isEmojiOnly(m.blocks)} />
          )}
          {m.deleted || m.huddle === undefined ? null : <HuddleMessage {...p} />}
          {m.deleted || m.shared === undefined ? null : (
            <SharedQuote
              shared={m.shared}
              ctx={p.ctx}
              onOpen={p.extras?.onOpenShared === undefined ? undefined : () => p.extras?.onOpenShared?.(m.shared!)}
              testID={`shared-${p.item.key}`}
            />
          )}
          {m.deleted || m.unfurls === undefined
            ? null
            : m.unfurls.map((u, i) => (
                <UnfurlCard
                  key={u.url}
                  unfurl={u}
                  onRemove={p.extras?.onHideUnfurl === undefined ? undefined : () => p.extras?.onHideUnfurl?.(u.url)}
                  testID={`unfurl-${p.item.key}-${i}`}
                />
              ))}
          {/* Grouped rows have no header to carry "(edited)": it goes under the text. */}
          {grouped && !editing && m.editedAt !== undefined && !m.deleted ? (
            <Text variant="caption" color="text.tertiary" testID={`edited-${p.item.key}`}>
              {t("message.edited")}
            </Text>
          ) : null}
          {m.deleted || m.files.length === 0 || p.fileActions === undefined ? null : (
            <MessageFiles files={m.files} actions={p.fileActions} pending={!sent} testID={`files-${p.item.key}`} />
          )}
          {m.deleted || m.sticker === undefined ? null : (
            <StickerMessage
              sticker={m.sticker}
              onPress={sent && p.onStickerPress !== undefined ? () => p.onStickerPress?.(m.sticker!.packId) : undefined}
              testID={`sticker-${p.item.key}`}
            />
          )}
          {m.deleted || m.gif === undefined ? null : <GifMessage gif={m.gif} testID={`gif-${p.item.key}`} />}
          <ReactionChips reactions={m.reactions} meId={p.meId} members={p.ctx.members} attention={attention} onToggle={react} testID={`reactions-${p.item.key}`} />
          {p.showThreadSummary && m.replyCount > 0 && p.onReply !== undefined ? <ThreadSummary item={p.item} ctx={p.ctx} onReply={p.onReply} /> : null}
          {sent ? null : <SendState item={p.item} offline={p.offline} uploadProgress={p.uploadProgress} onRetry={p.onRetry} onDiscard={p.onDiscard} />}
        </Stack>
      </Stack>
    </Stack>
  );
}
